import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { matchSearchIndexPath } from '../pages/index/match-search-index-path';

export type SrpCardDisplayMode = 'cards' | 'table';

/** Below this, the SRP results container can't fit the "Taulukko" table's 8 columns without
 *  horizontal scroll (title/company/location/salary/level/language/seen/view), so the table isn't
 *  offered at all (coordinator feedback, 2026-09-27: gate on the results container's own measured
 *  width, not viewport size or device/touch detection -- a container query in spirit, measured via
 *  ResizeObserver since CSS container queries can't drive a React branch/conditional render). */
export const TABLE_VIEW_MIN_CONTAINER_WIDTH = 1024;

interface SrpCardDisplayModeContextValue {
  /** The stored preference, untouched by container width -- a visitor's 'table' choice survives a
   *  temporary narrow window/container and comes back once it widens again. */
  mode: SrpCardDisplayMode;
  setMode: (mode: SrpCardDisplayMode) => void;
  /** True once the results container has been measured wide enough for the table. Starts true
   *  (permissive) so SSR/first paint never assumes narrow; CustomHits.tsx corrects it after mount
   *  via ResizeObserver on the actual results container. */
  tableAvailable: boolean;
  setTableAvailable: (available: boolean) => void;
  /** `mode`, corrected for container width: 'table' when the container is too narrow reads as
   *  'cards' here without touching the stored preference itself. Every renderer/consumer should
   *  use this, not `mode` directly, to decide what actually shows. */
  effectiveMode: SrpCardDisplayMode;
}

/**
 * SRP B2 redesign: whether the non-map view shows the JobCard grid ("Kortit", default,
 * unchanged) or the HitTableB2 spreadsheet-style table ("Taulukko"). Independent of the
 * list/map view mode (useViewMode), but both live in the same URL param: `?view=table` here,
 * `?view=map` there, cards by omission. URL-driven (not localStorage) so SSR renders the chosen
 * view directly: `initialMode` comes from the server's own parse of `?view=` (see
 * pages/index/+data.ts initialCardDisplayMode), and SearchContext's URL sync writes the param
 * back whenever `mode` changes. Routed in components/CustomHits.tsx (table needs to render as one
 * <table> spanning every hit, not a per-item routing decision like components/Hit/index.tsx's
 * compact-variant routing) and mirrored in the SRP's on-page view segmented control
 * (components/filters/SrpB2ViewToggle.tsx) so both read the same availability.
 */
const SrpCardDisplayModeContext = createContext<SrpCardDisplayModeContextValue>({
  mode: 'cards',
  setMode: () => {},
  tableAvailable: true,
  setTableAvailable: () => {},
  effectiveMode: 'cards',
});

function readModeFromUrl(): SrpCardDisplayMode {
  if (typeof window === 'undefined') return 'cards';
  return new URLSearchParams(window.location.search).get('view') === 'table' ? 'table' : 'cards';
}

export function SrpCardDisplayModeProvider({
  children,
  initialMode = 'cards',
}: {
  children: React.ReactNode;
  /** Server-parsed `?view=table` (the SRP island passes its view model's cardDisplayMode). */
  initialMode?: SrpCardDisplayMode;
}): React.ReactElement {
  const [mode, setModeState] = useState<SrpCardDisplayMode>(initialMode);
  const [tableAvailable, setTableAvailable] = useState(true);

  // Back/forward (and any other same-tab URL change) must restore the view the address bar says,
  // same as useViewMode does for map mode.
  useEffect(() => {
    // Only a search results URL carries this page's view. Clicking a job pushes the job URL while
    // the SRP is still mounted (and stays mounted with utils/navigation/srp-keep-alive.tsx), and
    // reading that as "no ?view" would drop table mode.
    const syncFromUrl = (): void => {
      if (!matchSearchIndexPath(window.location.pathname)) return;
      setModeState(readModeFromUrl());
    };
    window.addEventListener('popstate', syncFromUrl);
    window.addEventListener('urlchange', syncFromUrl);
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      window.removeEventListener('urlchange', syncFromUrl);
    };
  }, []);

  const setMode = useCallback((next: SrpCardDisplayMode) => {
    setModeState(next);
  }, []);

  const effectiveMode: SrpCardDisplayMode = mode === 'table' && !tableAvailable ? 'cards' : mode;

  return (
    <SrpCardDisplayModeContext.Provider value={{ mode, setMode, tableAvailable, setTableAvailable, effectiveMode }}>
      {children}
    </SrpCardDisplayModeContext.Provider>
  );
}

export function useSrpCardDisplayMode(): SrpCardDisplayModeContextValue {
  return useContext(SrpCardDisplayModeContext);
}
